import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { OrderDetails } from '../../services/orders.service';
import ordersService from '../../services/orders.service';
import { useToast } from '../../contexts/ToastContext';
import PermissionsContainer from '../PermissionsContainer';
import ReuseContainerDrawer from '../ReuseContainerDrawer';
import { computeReuseEligibility } from '../../utils/reuseEligibility';

interface OrderReuseActionsProps {
  orderDetails: OrderDetails;
  /** Drives the "N opportunities" badge on the Reuse/View buttons. Pass the
   * length of `useOrderEditing().streetTurnOpportunities`. */
  streetTurnOpportunitiesCount: number;
  /** When true, all three action buttons are disabled — used to defer reuse
   * actions while an unrelated save is in flight. */
  busy?: boolean;
  /** True when the order form has pending unsaved edits. When set, Reuse and
   * Undo Reuse run the order save first (via `onSaveBeforeAction`) and abort
   * if it's blocked — see the save-before-reuse note on `onSaveBeforeAction`. */
  hasUnsavedChanges?: boolean;
  /** Runs the full order save flow and resolves `true` only when it's safe to
   * proceed (saved cleanly, or nothing to save). Reuse / Undo await this when
   * there are unsaved changes so they never run against edits the user hasn't
   * committed. Mirrors the legacy CMS, where "Reuse Container" was a submit
   * button on the order edit form: the order saved first (a failed save
   * aborted the reuse) and the reuse ran against the just-saved data. Omit it
   * (or `hasUnsavedChanges`) to keep the old fire-immediately behavior. */
  onSaveBeforeAction?: () => Promise<boolean>;
  /** Invoked after a successful Reuse Container submission (drawer's
   * `onSuccess`) or after an Undo Reuse completes. Use to re-fetch order
   * details so the page reflects the new state. */
  onRefresh?: () => void;
  /** Optional follow-up fired after the undo flow completes (and after
   * `onRefresh`). The legacy drawer uses this to close itself once the
   * order has been un-reused; /pro leaves it omitted so the page stays. */
  onUndoSuccess?: () => void;
}

/**
 * Reusable Reuse / View / Undo action buttons. Mirrors the drawer's wiring
 * at OrderDetailsModalContent.tsx ~1155-1205 + ~1383-1421. Each button is
 * gated by the shared `computeReuseEligibility` rules and wrapped in the
 * `reuse-container` PermissionsContainer.
 *
 * Render the buttons in a horizontal row beside Save. The undo confirmation
 * dialog and the ReuseContainerDrawer are owned internally; the caller only
 * needs to pass order data and an `onRefresh` callback for post-action data
 * reload.
 */
const OrderReuseActions: React.FC<OrderReuseActionsProps> = ({
  orderDetails,
  streetTurnOpportunitiesCount,
  busy = false,
  hasUnsavedChanges = false,
  onSaveBeforeAction,
  onRefresh,
  onUndoSuccess,
}) => {
  const { showToast } = useToast();

  const [reuseDrawerOpen, setReuseDrawerOpen] = useState(false);
  const [viewReuseDrawerOpen, setViewReuseDrawerOpen] = useState(false);
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false);
  const [undoSubmitting, setUndoSubmitting] = useState(false);

  // Native <dialog> for the undo confirm — same pattern as the drawer, so it
  // stacks correctly above any parent modal layer.
  const undoDialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dlg = undoDialogRef.current;
    if (!dlg) return;
    if (undoConfirmOpen && !dlg.open) dlg.showModal();
    else if (!undoConfirmOpen && dlg.open) dlg.close();
  }, [undoConfirmOpen]);

  const eligibility = computeReuseEligibility(orderDetails, streetTurnOpportunitiesCount);

  // Save-before-reuse gate. Resolves `true` when it's safe to run the
  // reuse/undo — the order saved cleanly, or there was nothing to save. When
  // the save is blocked (validation, stale conflict, deadline/override
  // cancel, network error) it resolves `false` and the caller aborts. See the
  // `onSaveBeforeAction` prop note for the legacy parity this restores.
  const ensureSavedBeforeAction = useCallback(async (): Promise<boolean> => {
    if (!hasUnsavedChanges || !onSaveBeforeAction) return true;
    return onSaveBeforeAction();
  }, [hasUnsavedChanges, onSaveBeforeAction]);

  const handleUndoReuse = useCallback(async () => {
    setUndoSubmitting(true);
    // Commit point. Close the confirm dialog up front so the user lands back
    // on the pro and the outcome is reported via a toast (win or lose). This
    // also frees any save-time prompt — deadline reason / driver override, a
    // normal-layer portal — from behind this native top-layer <dialog>.
    setUndoConfirmOpen(false);
    try {
      // Save the order's pending edits first; abort the undo if that save is
      // blocked (the save flow surfaces its own error toast) so the undo never
      // runs against rejected edits. On success it runs against saved data.
      const saved = await ensureSavedBeforeAction();
      if (!saved) return;
      await ordersService.undoReuseContainer(orderDetails.pronumber);
      showToast({
        type: 'success',
        message: `PRO ${orderDetails.pronumber} reuse cancelled`,
      });
      onRefresh?.();
      onUndoSuccess?.();
    } catch (err: unknown) {
      const e = err as {
        response?: { data?: { detail?: unknown } };
        message?: string;
      };
      const detail = e?.response?.data?.detail as unknown;
      const msg = Array.isArray((detail as { errors?: unknown })?.errors)
        ? ((detail as { errors: string[] }).errors.join('; '))
        : typeof detail === 'string'
          ? detail
          : e?.message || 'Undo reuse failed';
      showToast({ type: 'error', message: `Undo failed: ${msg}` });
    } finally {
      setUndoSubmitting(false);
    }
  }, [orderDetails.pronumber, onRefresh, onUndoSuccess, showToast, ensureSavedBeforeAction]);

  return (
    <>
      {eligibility.canReuse && (
        <PermissionsContainer
          componentId="reuse-container"
          componentName="Reuse Container"
          defaultHidden
        >
          <button
            type="button"
            onClick={() => setReuseDrawerOpen(true)}
            disabled={eligibility.disabledReason !== null || busy}
            title={eligibility.disabledReason || 'Reuse this container on another pro'}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors"
          >
            Reuse Container
            {streetTurnOpportunitiesCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-white/20 text-white"
                title={`${streetTurnOpportunitiesCount} street turn opportunit${streetTurnOpportunitiesCount === 1 ? 'y' : 'ies'}`}
              >
                {streetTurnOpportunitiesCount}
              </span>
            )}
          </button>
        </PermissionsContainer>
      )}

      {eligibility.canViewReuse && (
        <PermissionsContainer
          componentId="reuse-container"
          componentName="Reuse Container"
          defaultHidden
        >
          <button
            type="button"
            onClick={() => setViewReuseDrawerOpen(true)}
            disabled={busy}
            title="View candidate import pros flagged for street turn"
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors"
          >
            View Reuse Options
            <span
              className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-white/20 text-white"
              title={`${streetTurnOpportunitiesCount} street turn opportunit${streetTurnOpportunitiesCount === 1 ? 'y' : 'ies'}`}
            >
              {streetTurnOpportunitiesCount}
            </span>
          </button>
        </PermissionsContainer>
      )}

      {eligibility.canUndoReuse && (
        <PermissionsContainer
          componentId="reuse-container"
          componentName="Reuse Container"
          defaultHidden
        >
          <button
            type="button"
            onClick={() => setUndoConfirmOpen(true)}
            disabled={busy || undoSubmitting}
            className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors"
          >
            {undoSubmitting ? 'Undoing…' : 'Undo Reuse Container'}
          </button>
        </PermissionsContainer>
      )}

      <ReuseContainerDrawer
        pronumber={orderDetails.pronumber}
        importEquipType={orderDetails.equipType}
        mode="import"
        isOpen={reuseDrawerOpen}
        onClose={() => setReuseDrawerOpen(false)}
        onSaveBeforeReuse={ensureSavedBeforeAction}
        onSuccess={() => onRefresh?.()}
      />
      <ReuseContainerDrawer
        pronumber={orderDetails.pronumber}
        mode="export"
        isOpen={viewReuseDrawerOpen}
        onClose={() => setViewReuseDrawerOpen(false)}
      />

      <dialog
        ref={undoDialogRef}
        onCancel={(e) => {
          e.preventDefault();
          if (!undoSubmitting) setUndoConfirmOpen(false);
        }}
        className="bg-transparent p-0 backdrop:bg-gray-900/50 backdrop:backdrop-blur-sm rounded-lg"
      >
        <div className="bg-panel rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
          <h3 className="text-base font-semibold text-heading mb-2">
            Undo Reuse Container
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            This will undo the street turn between PRO {orderDetails.pronumber} and
            PRO {orderDetails.reuseContainerPro}. The container will be moved back
            to the import side and the export pickup leg restored. Continue?
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setUndoConfirmOpen(false)}
              disabled={undoSubmitting}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUndoReuse}
              disabled={undoSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 rounded-md"
            >
              {undoSubmitting ? 'Undoing…' : 'Undo Reuse'}
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
};

export default OrderReuseActions;